import { useUnitChoices } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { PageHeader } from "@shared/components/PageHeader";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { toast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { HelpCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAllCriticality,
  useGetCriticalityByType,
  useGetSystemDataById,
  useSystemParameters,
  useUpdateSystems,
} from "../../hooks";
import { SystemFormData } from "../../types";
import { getCriticalityLevel } from "../../utils";

interface StepHelpers {
  getStepData: (stepIndex: number) => any;
  setStepData: (stepIndex: number, data: any) => void;
}

interface OperationsStepProps {
  editMode: boolean;
  systemId?: string; // For edit mode
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  globalFormData: SystemFormData;
  updateGlobalFormData: (data: Partial<SystemFormData>) => void;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const OperationsStep: React.FC<OperationsStepProps> = ({
  editMode,
  systemId,
  stepHelpers,
  currentStepIndex = 1,
  globalFormData,
  updateGlobalFormData,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { systemsId, action } = useParams();
  const navigate = useNavigate();

  // API hooks
  const { data: systemData } = useGetSystemDataById(Number(systemsId));
  const { data: unitList } = useUnitChoices();
  const { data: criticality } = useGetAllCriticality();
  const { data: systemParameters } = useSystemParameters(
    systemData?.systemType
  );
  const { data: criticalityParameters } = useGetCriticalityByType(
    systemData?.systemType
  );
  // API mutation hooks
  const { mutateAsync: updateSystem, isPending: isUpdating } = useUpdateSystems(
    Number(systemsId)
  );

  // Initialize dynamic criticality fields with default value of 3
  const initializeDynamicFields = useMemo(() => {
    if (!criticality || !Array.isArray(criticality)) return {};

    const dynamicFields: any = {};
    criticality.forEach((item) => {
      const fieldKey = item.code;
      dynamicFields[fieldKey] = 3; // Default to 3
    });

    return dynamicFields;
  }, [criticality]);

  // Local state for form data
  const [localFormData, setLocalFormData] = useState({
    designCapacity: 0,
    capacityUnit: "",
    currentThroughput: 0,
    monitoredParameters: [],
    ...initializeDynamicFields,
  });

  // ONLY autopopulate when action is "edit" and systemData is available
  useEffect(() => {
    if (action === "edit" && systemData) {
      // Extract criticality scores from systemData.criticality_assessment
      const criticalityScores: any = {};
      if (
        systemData.criticalityAssessment &&
        Array.isArray(systemData.criticalityAssessment)
      ) {
        systemData.criticalityAssessment.forEach((item: any) => {
          if (item.code && item.score !== undefined) {
            // Extract the field key from the code (everything before the #)
            const fieldKey = item.code;
            criticalityScores[fieldKey] = item.score;
          }
        });
      } else {
        if (
          criticalityParameters?.[0]?.criticalityAssessment &&
          Array.isArray(criticalityParameters?.[0]?.criticalityAssessment)
        ) {
          criticalityParameters?.[0]?.criticalityAssessment?.forEach(
            (item: any) => {
              if (item.code && item.score !== undefined) {
                // Extract the field key from the code (everything before the #)
                const fieldKey = item.code;
                criticalityScores[fieldKey] = item.score;
              }
            }
          );
        }
      }

      const operationsData = {
        designCapacity: parseFloat(systemData?.designCapacity) || 0,
        capacityUnit: systemData?.capacityUnit || "",
        currentThroughput: parseFloat(systemData?.currentThroughput) || 0,
        monitoredParameters:
          systemData?.monitoredParameters?.map((param) => ({
            name: param.name,
            unit: param.unit,
            value: String(param.value) ?? null,
          })) || [],
        // For dynamic criticality fields, use scores from criticality_assessment or default to 3
        ...Object.keys(initializeDynamicFields).reduce((acc, key) => {
          acc[key] =
            criticalityScores[key] !== undefined ? criticalityScores[key] : 3;
          return acc;
        }, {} as any),
      };

      setLocalFormData(operationsData);
      onDataChange?.(operationsData);
      updateGlobalFormData(operationsData);

      // Set form values directly
      try {
        form.setValue(
          "designCapacity",
          parseFloat(systemData?.designCapacity) || 0
        );
        form.setValue("capacityUnit", systemData?.capacityUnit || "");
        form.setValue(
          "currentThroughput",
          parseFloat(systemData?.currentThroughput) || 0
        );
        form.setValue(
          "monitoredParameters",
          systemData?.monitoredParameters || []
        );

        // Set dynamic criticality field values from criticality_assessment
        Object.keys(initializeDynamicFields).forEach((key) => {
          const score =
            criticalityScores[key] !== undefined ? criticalityScores[key] : 3;
          form.setValue(key, score);
        });
      } catch (error) {}
    }
  }, [
    action,
    systemData,
    initializeDynamicFields,
    systemParameters,
    criticalityParameters,
  ]);

  const capacityUnitOptions = useMemo(() => {
    return (
      unitList?.map((unit: any) => ({
        value: unit.key,
        label: unit.value,
      })) || []
    );
  }, [unitList]);

  const getCriticalityBadgeVariant = (level: string) => {
    switch (level) {
      case "High":
        return "destructive";
      case "Medium":
        return "secondary";
      case "Low":
        return "outline";
      default:
        return "outline";
    }
  };

  const getCriticality = (score: number) => {
    if (score >= 0 && score <= 25) {
      return { level: "Low", variant: "success" }; // green
    } else if (score <= 50) {
      return { level: "Medium", variant: "warning" }; // yellow
    } else if (score <= 75) {
      return { level: "High", variant: "destructive" }; // red
    } else {
      return { level: "Critical", variant: "secondary" };
    }
  };

  // Generate criticality slider fields dynamically from API data
  const generateCriticalityFields = useMemo((): FormField[] => {
    if (!criticality || !Array.isArray(criticality)) return [];

    return criticality
      .filter((item) => item.isActive === true)
      .map((item) => {
        const fieldKey = item.code;

        return {
          name: fieldKey,
          type: "slider" as const,
          label: `${item.name} (${item.weightage}%)`,
          min: 1,
          max: 5,
          step: 1,
          defaultValue: 3,
          group: "Criticality Assessment",
          groupColorNeeded: false,
          showHelperTooltip: true,
          tooltipText: item.description || `Assessment for ${item.name}`,
          className: "col-span-2",
        } as FormField;
      });
  }, [criticality]);

  // Define form fields for monitored parameters using form-array
  const monitoredParameterItemFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Parameter Name",
      placeholder: "Enter parameter name",
      required: false,
      className: "col-span-1",
    },
    {
      name: "unit",
      type: "text",
      label: "Unit",
      placeholder: "Enter unit",
      required: false,
      className: "col-span-1",
    },
    {
      name: "value",
      type: "text",
      label: "Value",
      placeholder: "Enter value",
      required: false,
      className: "col-span-1",
    },
  ];

  // Create form fields
  const formFields: FormField[] = [
    // Design Capacity Group
    {
      name: "designCapacity",
      type: "decimal",
      label: "Design Capacity",
      placeholder: "Enter capacity",
      required: true,
      group: "Design Capacity",
      groupColorNeeded: false,
      showHelperTooltip: true,
      tooltipText:
        "Design Capacity Enter the maximum throughput or volume that the system was designed to handle under normal operating conditions, using the appropriate units selected from the dropdown menu. This figure comes from the original specifications and represents the theoretical maximum sustainable capacity.",
      className: "col-span-1",
    },
    {
      name: "capacityUnit",
      type: "select",
      label: "Capacity Unit",
      required: true,
      group: "Design Capacity",
      showHelperTooltip: true,
      tooltipText:
        "Capacity Unit Select the appropriate unit of measurement for the design capacity, such as cubic meters, gallons, or cubic feet. This unit should align with the system's intended capacity and flow rate.",
      className: "col-span-1",
      options: capacityUnitOptions,
    },

    // Monitored Parameters Group
    {
      name: "monitoredParameters",
      type: "form-array",
      label: "",
      group: "Monitored Parameters",
      groupColorNeeded: false,
      itemFields: monitoredParameterItemFields,
      showAddButton: true,
      showRemoveButton: true,
      showReorderButtons: false,
      addButtonText: "Add Parameter",
      emptyMessage:
        "No parameters available. Please select a system type first or add parameters manually.",
      helperText:
        "Pre-populated per system type. Enter values for each parameter.",
      className: "col-span-full",
    },

    // Dynamic Criticality Assessment Group
    ...generateCriticalityFields,
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  useEffect(() => {
    if (systemParameters && systemData?.monitoredParameters?.length === 0) {
      const systemParametersList = systemParameters.map((item) => ({
        name: item.name,
        unit: item.unit,
        value: item.value ? String(item.value) : "",
      }));

      form.setValue("monitoredParameters", systemParametersList || []);
    }
  }, [action, systemData, systemParameters, form]);

  const criticalityKeys = useMemo(
    () => (criticality ?? []).filter((i) => i.isActive).map((i) => i.code),
    [criticality]
  );

  // Watch slider fields so component re-renders when they change
  const watchedValues = form.watch(criticalityKeys);

  const { score: criticalityScore, level: criticalityLevel } = useMemo(() => {
    try {
      if (!criticality) return { score: 0, level: "Low" };

      const activeItems = criticality.filter((item) => item.isActive === true);
      if (activeItems.length === 0) return { score: 0, level: "Low" };

      let totalWeightage = 0;
      let weightedSum = 0;

      activeItems.forEach((item) => {
        const fieldKey = item.code;
        const scoreValue = form.getValues(fieldKey); // same logic you had

        if (
          scoreValue !== undefined &&
          scoreValue !== null &&
          !isNaN(Number(scoreValue))
        ) {
          const weight = item.weightage / 100;
          weightedSum += Number(scoreValue) * weight;
          totalWeightage += weight;
        }
      });

      const criticalityScore =
        totalWeightage > 0 ? weightedSum / totalWeightage : 0;

      let criticalityLevel;
      if (typeof getCriticalityLevel === "function") {
        criticalityLevel = getCriticalityLevel(criticalityScore) || "Low";
      } else {
        if (criticalityScore >= 4) criticalityLevel = "High";
        else if (criticalityScore >= 3) criticalityLevel = "Medium";
        else if (criticalityScore >= 2) criticalityLevel = "Medium";
        else if (criticalityScore >= 1) criticalityLevel = "Low";
        else criticalityLevel = "Low";
      }

      return { score: criticalityScore, level: criticalityLevel };
    } catch (error) {
      return { score: 0, level: "Low" };
    }
  }, [criticality, watchedValues]);

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    // Handle array operations for monitoredParameters
    if (fieldName === "monitoredParameters") {
      const updatedData = {
        ...localFormData,
        monitoredParameters: value,
      };

      setLocalFormData(updatedData);
      updateGlobalFormData({ monitoredParameters: value });
      onDataChange?.({ ...data, monitoredParameters: value });
      return;
    }

    // Handle all other fields (including dynamic criticality fields)
    const updatedData = {
      ...localFormData,
      [fieldName]: value,
    };

    setLocalFormData(updatedData);
    updateGlobalFormData({ [fieldName]: value });
    onDataChange?.({ ...data, [fieldName]: value });
  };

  const handleNext = async () => {
    const isValid = await FormService.validateForm(form);

    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      toast({
        title: "Please fill in all required fields.",
        variant: "destructive",
      });

      return;
    }

    const formData = form.getValues();
    if (formData.designCapacity === "0") {
      toast({
        title: "Design capacity can not be 0",
        variant: "destructive",
      });
      return;
    }
    try {
      // Build criticality assessment array
      const criticalityAssessment =
        criticality
          ?.filter((item) => item.isActive === true)
          .map((item) => {
            const fieldKey = item.code;
            return {
              name: item.name,
              score: formData[fieldKey] || 3,
              code: item.code,
              weight: item.weightage,
            };
          }) || [];

      const payload = {
        design_capacity: parseFloat(formData.designCapacity),
        capacity_unit: formData.capacityUnit,
        monitored_parameters: formData.monitoredParameters || [],
        criticality_assessment: criticalityAssessment,
        remote_utility_id: getRemoteUtilityId(),
      };

      let response;
      let systemDraftId;

      if (action === "edit") {
        response = await updateSystem(payload);
        systemDraftId =
          response?.id ||
          stepHelpers?.getStepData(0)?.systemDraftId ||
          systemsId;
      } else {
        systemDraftId = stepHelpers?.getStepData(0)?.systemDraftId;
      }

      const completedData = {
        ...localFormData,
        payload,
        systemDraftId,
        response,
        isStepComplete: false,
        completedAt: new Date().toISOString(),
      };

      onDataChange?.(completedData);
      onNext?.();

      if (action === "edit" && systemDraftId) {
        navigate(`/ax/asset-registry/systems/edit/${systemDraftId}#docs`);
      }
    } catch (error) {
      if (setValidationError) {
        setValidationError(
          "Failed to submit operations data. Please try again."
        );
      }
    }
  };

  const handlePrevious = () => {
    onPrevious?.();
  };

  const { level, variant } = getCriticality(criticalityScore * 20);

  // Custom footer with navigation buttons and criticality score display
  const renderCustomFooter = () => (
    <>
      {/* Criticality Score Display */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-700">
                Final Criticality Score
              </p>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Final Criticality Score This score is automatically
                      calculated based on the weighted average of all assessed
                      parameters, providing an overall measure of how critical
                      this system is to your organization's operations. The
                      score helps prioritize maintenance activities, emergency
                      response planning, and resource allocation decisions
                      across your entire system portfolio.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-2xl font-bold">
              {Math.round(criticalityScore * 20)}
            </p>
          </div>
          <Badge variant={variant}>{level}</Badge>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 mt-6 border-t border-gray-200">
        <Button
          onClick={handlePrevious}
          disabled={isFirstStep}
          variant="outline"
        >
          Previous
        </Button>
        <Button onClick={handleNext} disabled={isUpdating}>
          {isUpdating ? "Saving..." : isLastStep ? "Complete" : "Next"}
        </Button>
      </div>
    </>
  );

  return (
    <div className="bg-white p-3">
      <div className="mb-2">
        <PageHeader
          title="Operations Configuration"
          subtitle="Specify the system's design capacity, monitored parameters, and criticality assessment."
        />
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        responsiveColumns={{
          sm: 1,
          md: 2,
        }}
        renderCustomFooter={renderCustomFooter}
        config={{
          layout: "grid",
          columns: 2,
          showResetButton: false,
          showSubmitButton: false,
          spacing: "medium",
          classes: {
            form: "space-y-8 p-0",
            button: "px-6 py-2 rounded-md font-medium transition-colors",
            buttonContainer: "flex justify-between",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
            slider: "w-full",
            label: "block text-sm font-medium text-gray-700",
            error: "text-sm text-red-600 mt-1",
            container: "space-y-1",
            helperText: "text-sm text-gray-500 mt-1",
            textarea:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
          },
        }}
      />
    </div>
  );
};
